import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Briefcase, Clock, CalendarClock, Wallet,
  FileText, LogOut, Moon, Sun, Menu, ClipboardList, Users, Percent, Receipt, BadgeCheck,
} from "lucide-react";
import makLogo from "@/assets/mak-logo.png";
import makLogoWhite from "@/assets/mak-logo-white.png";

const NAV = [
  { to: "/", label: "Pregled", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/kupci", label: "Kupci & Poslovi", icon: Briefcase, testid: "nav-deals" },
  { to: "/sihtarica", label: "Šihtarica", icon: Clock, testid: "nav-timesheet" },
  { to: "/rekapitulacija", label: "Rekapitulacija rada", icon: ClipboardList, testid: "nav-rekap" },
  { to: "/radnici", label: "Radnici", icon: Users, testid: "nav-workers" },
  { to: "/rokovi", label: "Rokovi", icon: CalendarClock, testid: "nav-deadlines" },
  { to: "/fakture", label: "Fakture dobavljača", icon: FileText, testid: "nav-invoices" },
  { to: "/pos", label: "POS računi", icon: Receipt, testid: "nav-pos" },
  { to: "/troskovi", label: "Mjesečni troškovi", icon: Wallet, testid: "nav-expenses" },
  { to: "/pdv", label: "Povrat PDV-a", icon: Percent, testid: "nav-pdv" },
  { to: "/porijeklo", label: "Izjava o porijeklu", icon: BadgeCheck, testid: "nav-porijeklo" },
];

function NavLinks({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onNavigate}
            data-testid={`${item.testid}-link`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-slate-600 hover:bg-secondary hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-5 h-16 border-b">
      <img src={makLogo} alt="MAK" className="h-10 w-auto dark:hidden" />
      <img src={makLogoWhite} alt="MAK" className="h-10 w-auto hidden dark:block" />
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-left">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r bg-card fixed inset-y-0 left-0 z-30">
        <Brand />
        <div className="flex-1 overflow-y-auto py-4">
          <NavLinks />
        </div>
        <div className="border-t p-3">
          <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground truncate">{user?.email}</div>
          <Button variant="outline" size="sm" className="w-full justify-start" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Odjava
          </Button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-16 border-b bg-card/95 backdrop-blur flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 bg-card">
                <Brand />
                <div className="py-4"><NavLinks /></div>
              </SheetContent>
            </Sheet>
            <span className="font-heading font-semibold text-sm text-muted-foreground hidden sm:block">Upravljanje poslovanjem</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle-btn">
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
